import EventEmitter from "eventemitter3";
import components from "../components/_map";
import { debounce } from "lodash";
import GridDebug from '../utils/GridDebug';
import gsap from "gsap/all";
import { CustomEase, MotionPathPlugin } from "gsap/all";
import Utils from "../utils/utils";
import Dropdowns from "../components/dropdowns";
import SiteMenu from "../components/site-menu";
import LCS from "../components/lenis-scroll";
import PageTransition from '../transitions/page-transition';
import barba from "@barba/core";
import { initContactFormTracking } from '../utils/tracking';

gsap.registerPlugin(MotionPathPlugin);
gsap.registerPlugin(CustomEase);

export default class AppController extends EventEmitter {
	constructor() {
		super();
		if (!AppController.singleton) {
			AppController.singleton = this;
			this.components = [];
			this.gsap = gsap;
			this.currentController = document.body.getAttribute('data-controller');
			this.components_map = components;
			this.init();
			return AppController.singleton;
		}
	}

	init() {
		this.isMobile = window.matchMedia('(max-width: 1024px), (pointer: coarse)').matches;
		this.$scroll = this.isMobile ? null : new LCS();
		// this.grid = new GridDebug(14, 0);

		this.initAppEvents();
		this.initDomElements();
		this.instantiateComponents();

		this.$dropdowns = new Dropdowns();
		this.initViewportVhVar();
		this.initDetect();
		this.initTextareas();
		this.$menu = new SiteMenu(this);
		this.$pageTransition = new PageTransition(this);
		this.initButtonHoverEffects();
		initContactFormTracking();
		this.initBarba();

		setTimeout(() => {
			this.initContactForm7();
		}, 100);
	}

	initDomElements() {
	}

	initAppEvents() {
		window.addEventListener("resize", () => {
			debounce((event) => {
				this.emit("resize", event);
			}, 16);
		});

		window.addEventListener("scroll", (event) => {
			this.emit("scroll", event);
		});

		window.onhashchange = () => {
			this.emit("hash-change", window.location.href);
		};

		this.on("scrollTo", (elt) => {
			if (this.$scroll) {
				this.$scroll.scrollTo(elt);
			} else {
				window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
			}
		});

		this.on("hide-navbar", () => {
			if (this.$navbarContainer) {
				this.$navbarContainer.classList.add('hidden');
			}
		});

		this.on("show-navbar", () => {
			if (this.$navbarContainer) {
				this.$navbarContainer.classList.remove('hidden');
			}
		});

		this.on("disable-scroll", () => {
			if (this.$scroll) {
				this.$scroll.disable();
			}
		});

		this.on("enable-scroll", () => {
			if (this.$scroll) {
				this.$scroll.enable();
			}
		});

		this.on('remove-bg-navbar', () => {
			if (this.$navbar && this.$navbarContainer) {
				if (!this.$navbar.classList.contains('--scrolling')) {
					this.$navbarContainer.classList.remove('bg-white');
				}
			}
		});
	}

	initTextareas() {
		const tx = document.getElementsByTagName("textarea");
		for (let i = 0; i < tx.length; i++) {
			tx[i].setAttribute("style", "height:" + (tx[i].scrollHeight) + "px;overflow-y:hidden;");
			tx[i].addEventListener("input", OnInput, false);
		}

		function OnInput() {
			this.style.height = "auto";
			this.style.height = (this.scrollHeight) + "px";
		}
	}

	instantiateComponents() {
		const nodes = document.body.querySelectorAll('[data-component]');
		nodes.forEach((el) => {
			const alreadyInstantiated = this.components.some(component => component.$el === el);
			if (alreadyInstantiated) {
				return;
			}

			const name = el.getAttribute('data-component');
			const mod = this.components_map[name];
			if (!mod) return;

			const Ctor = mod.default || mod;
			if (typeof Ctor === 'function') {
				this.components.push(new Ctor(el));
			}
		});
	}

	initViewportVhVar() {
		this.updateViewportVhVar();
		window.addEventListener("resize", () => {
			this.updateViewportVhVar();
		});
	}

	updateViewportVhVar() {
		let vh = window.innerHeight;
		document.documentElement.style.setProperty('--vh', `${vh / 100}px`);
	}

	initDetect() {
		window.addEventListener("resize", () => {
			this.detectDevice();
		});
		this.detectDevice();
	}

	detectDevice = () => {
		let landscape = window.matchMedia("(orientation: landscape)");
		document.documentElement.setAttribute('data-device-notice', Utils.deviceType() == 'mobile' && landscape.matches && window.innerWidth < 1000);
	}

	initBarba() {
		barba.hooks.afterLeave((data) => {
			const js = data.next.container.querySelectorAll("script.reload-on-ajax");
			[].forEach.call(js, function (script) {
				try {
					window.eval(script.innerHTML);
				} catch (e) {
					// Silent fail
				}
			});
		});

		barba.hooks.after(() => {
			if (this.$scroll) {
				this.$scroll.update();
				if (this.$scroll.lenis) {
					this.$scroll.lenis.scrollTo(0, { immediate: true });
				} else if (this.$scroll.scrollTo) {
					this.$scroll.scrollTo(0, { immediate: true });
				}
			}
		});

		barba.hooks.afterEnter((data) => {
			try {
				const title = data?.next?.container?.getAttribute("data-page-title");
				if (title) {
					document.title = title;
				}

				if (this.$pageTransition) {
					const canonical = this.$pageTransition._nextCanonical;
					const lang = this.$pageTransition._nextLang;
					const dir = this.$pageTransition._nextDir;
					if (canonical) {
						let link = document.querySelector('link[rel="canonical"]');
						if (!link) {
							link = document.createElement('link');
							link.rel = 'canonical';
							document.head.appendChild(link);
						}
						link.href = canonical;
					}
					if (lang != null) {
						document.documentElement.setAttribute('lang', lang);
					}
					if (dir != null) {
						document.documentElement.setAttribute('dir', dir);
					}
					this.$pageTransition._nextCanonical = null;
					this.$pageTransition._nextLang = null;
					this.$pageTransition._nextDir = null;
				}

				// 2. Virtual pageview pour GA4 via GTM
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: "virtual_pageview",
					page_path: window.location.pathname + window.location.search,
					page_title: document.title
				});
			} catch (e) {
				// Silent fail
			}
		});

		barba.init({
			debug: true,
			timeout: 10000,
			cacheIgnore: true,
			prevent: ({ el }) => {
				if (el && (el.hash || el.hasAttribute('data-popin-hash'))) {
					return true;
				}
				if (el && el.href) {
					try {
						const url = new URL(el.href, window.location.href);
						const currentUrl = new URL(window.location.href);
						if (url.pathname === currentUrl.pathname &&
							url.search === currentUrl.search &&
							url.hash !== currentUrl.hash) {
							return true;
						}
					} catch (e) {
						// Silent fail
					}
				}
				return el.getAttribute('href') == '#' || (el.getAttribute('href') && el.getAttribute('href').includes('billet'));
			},
			transitions: [
				{
					name: 'default',
					leave: (data) => {
						if (document.activeElement) document.activeElement.blur();
						document.querySelectorAll('.c-site-menu :focus').forEach(el => el.blur?.());

						if (this.$dropdowns?.closeAllDropdowns) {
							this.$dropdowns.closeAllDropdowns();
						}

						this.emit('close-menu');

						const TRANSITION_TIMEOUT_MS = 8000;
						const transitionPromise = new Promise((resolve) => {
							if (this.$pageTransition) {
								this.$pageTransition.show().then(() => {
									this.components?.forEach((c) => c?.destroy?.());
									this.components = [];
									if (this.$dropdowns?.destroy) {
										this.$dropdowns.destroy();
									}
									resolve();
								});
							} else {
								this.components?.forEach((c) => c?.destroy?.());
								this.components = [];
								if (this.$dropdowns?.destroy) {
									this.$dropdowns.destroy();
								}
								resolve();
							}
						});
						const timeoutPromise = new Promise((resolve) => {
							setTimeout(resolve, TRANSITION_TIMEOUT_MS);
						});
						return Promise.race([transitionPromise, timeoutPromise]).then(() => {
							this.components?.forEach((c) => c?.destroy?.());
							this.components = [];
						});
					},
					enter: async (data) => {
						if (this.$pageTransition) {
							await this.$pageTransition.updateMenuAndPopinsBeforeTransition(data);
						}

						if (this.$dropdowns?.init) {
							this.$dropdowns.init();
						} else if (this.$dropdowns?.refresh) {
							this.$dropdowns.refresh();
						} else {
							this.$dropdowns = new Dropdowns();
						}

						this.emit("remove-bg-navbar");
						this.instantiateComponents();
						this.initTextareas();

						if (data?.next?.html) {
							Utils.setNewAttrs(data.next.html);
						}
					},
					after: async () => {
						if (this.$pageTransition) {
							await this.$pageTransition.hide();
						}

						this.updateViewportVhVar();
						this.detectDevice();
						initContactFormTracking();
						setTimeout(() => {
							this.initContactForm7();
						}, 300);
					}
				}
			]
		});
	}

	initContactForm7(retryCount = 0) {
		if (retryCount > 20) {
			return;
		}

		if (typeof window.wpcf7 === 'undefined' || !window.wpcf7.init) {
			setTimeout(() => {
				this.initContactForm7(retryCount + 1);
			}, 100);
			return;
		}

		const cf_selector = 'div.wpcf7 > form';

		if (typeof window.jQuery !== 'undefined' && window.jQuery) {
			const cf_forms = window.jQuery(cf_selector);
			if (cf_forms.length > 0) {
				window.jQuery(cf_selector).each(function () {
					const $form = window.jQuery(this);
					if ($form[0] && $form[0].action) {
						$form[0].action = $form[0].action.replace(/#.+$/, '');
					}
					if (window.wpcf7 && window.wpcf7.init) {
						try {
							window.wpcf7.init($form[0]);
						} catch (e) {
							// Silent fail
						}
					}
				});
			}
		} else {
			const cf_forms = document.querySelectorAll(cf_selector);
			if (cf_forms.length > 0 && window.wpcf7 && window.wpcf7.init) {
				cf_forms.forEach(function (formEl) {
					if (formEl && formEl.action) {
						formEl.action = formEl.action.replace(/#.+$/, '');
					}
					try {
						window.wpcf7.init(formEl);
					} catch (e) {
						// Silent fail
					}
				});
			}
		}
	}

	initButtonHoverEffects() {
		if (AppController.__buttonsHoverBound) return;
		AppController.__buttonsHoverBound = true;

		const HOVER_IN_CLASS = 'is-hovering';
		const LEAVE_CLASS = 'is-leaving';
		const INSTANT_RESET = 'is-instant-reset';

		const timers = new WeakMap();
		const getBtn = (t) => (t && t.closest) ? t.closest('.c-button') : null;

		const clearTimer = (btn) => {
			const t = timers.get(btn);
			if (t) { clearTimeout(t); timers.delete(btn); }
		};

		document.addEventListener('mouseover', (e) => {
			const btn = getBtn(e.target);
			if (!btn) return;
			if (e.relatedTarget instanceof Element && btn.contains(e.relatedTarget)) return;

			clearTimer(btn);
			btn.classList.remove(LEAVE_CLASS);
			btn.classList.add(INSTANT_RESET);

			requestAnimationFrame(() => {
				void btn.offsetWidth;
				requestAnimationFrame(() => {
					btn.classList.remove(INSTANT_RESET);
					btn.classList.add(HOVER_IN_CLASS);
				});
			});
		}, { passive: true });

		document.addEventListener('mouseout', (e) => {
			const btn = getBtn(e.target);
			if (!btn) return;
			if (e.relatedTarget instanceof Element && btn.contains(e.relatedTarget)) return;

			btn.classList.remove(HOVER_IN_CLASS);
			btn.classList.add(LEAVE_CLASS);
		}, { passive: true });

		document.addEventListener('focusin', (e) => {
			const btn = getBtn(e.target);
			if (!btn) return;

			btn.classList.remove(LEAVE_CLASS);
			btn.classList.add(INSTANT_RESET);
			requestAnimationFrame(() => {
				void btn.offsetWidth;
				requestAnimationFrame(() => {
					btn.classList.remove(INSTANT_RESET);
					btn.classList.add(HOVER_IN_CLASS);
				});
			});
		});

		document.addEventListener('focusout', (e) => {
			const btn = getBtn(e.target);
			if (!btn) return;
			btn.classList.remove(HOVER_IN_CLASS);
			btn.classList.add(LEAVE_CLASS);
		});
	}

	static getInstance() {
		return AppController.singleton;
	}
}
